import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
    Mail, 
    Phone, 
    MapPin, 
    Send,
    Clock,
    CheckCircle2,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SEOHead, { pageSEO, generateBreadcrumbSchema } from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.message) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${API}/contact`, formData);
            toast.success(response.data.message);
            setSubmitted(true);
            setFormData({ name: '', email: '', phone: '', message: '' });
        } catch (error) {
            toast.error('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen" data-testid="contact-page">
            <SEOHead 
                title={pageSEO.contact.title}
                description={pageSEO.contact.description}
                keywords={pageSEO.contact.keywords}
                canonical={pageSEO.contact.canonical}
                schema={generateBreadcrumbSchema([
                    { name: 'Home', url: '/' },
                    { name: 'Contact', url: '/contact' }
                ])}
            />
            {/* Hero Section */}
            <section className="relative py-20 md:py-28 bg-primary/5">
                <div className="container-custom">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-2 mb-6">
                            <Mail className="w-8 h-8 text-primary" />
                            <span className="text-primary font-medium text-lg">Get in Touch</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight font-serif">
                            Contact Craven Canines
                        </h1>
                        <p className="text-xl text-muted-foreground leading-relaxed">
                            Ready to book a mantrailing session near Malmesbury? Have questions about our 
                            dog training services in Wiltshire? I'd love to hear from you!
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Form & Info */}
            <section className="section-spacing" data-testid="contact-section">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-5 gap-12">
                        {/* Contact Form */}
                        <div className="lg:col-span-3">
                            <Card className="shadow-lg border-0">
                                <CardContent className="p-8">
                                    {submitted ? (
                                        <div className="text-center py-12" data-testid="contact-success">
                                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <CheckCircle2 className="w-10 h-10 text-primary" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-foreground mb-4 font-serif">
                                                Message Sent!
                                            </h3>
                                            <p className="text-muted-foreground mb-6">
                                                Thank you for getting in touch. I'll get back to you as soon as possible.
                                            </p>
                                            <Button 
                                                onClick={() => setSubmitted(false)}
                                                variant="outline"
                                                className="rounded-full"
                                            >
                                                Send Another Message
                                            </Button>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <Label htmlFor="name" className="text-foreground">
                                                        Name <span className="text-accent">*</span>
                                                    </Label>
                                                    <Input
                                                        id="name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        placeholder="Your name"
                                                        className="input-default"
                                                        required
                                                        data-testid="contact-name-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="email" className="text-foreground">
                                                        Email <span className="text-accent">*</span>
                                                    </Label>
                                                    <Input
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        placeholder="your@email.com"
                                                        className="input-default"
                                                        required
                                                        data-testid="contact-email-input"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label htmlFor="phone" className="text-foreground">
                                                    Phone (optional)
                                                </Label>
                                                <Input
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="+44 1234 567890"
                                                    className="input-default"
                                                    data-testid="contact-phone-input"
                                                />
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label htmlFor="message" className="text-foreground">
                                                    Message <span className="text-accent">*</span>
                                                </Label>
                                                <Textarea
                                                    id="message"
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    placeholder="Tell me about your dog and what you're looking for..."
                                                    className="input-default min-h-[150px] resize-none"
                                                    required
                                                    data-testid="contact-message-input"
                                                />
                                            </div>
                                            
                                            <Button 
                                                type="submit" 
                                                className="w-full btn-primary"
                                                disabled={loading}
                                                data-testid="contact-submit-button"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="w-4 h-4 mr-2" />
                                                        Send Message
                                                    </>
                                                )}
                                            </Button>
                                        </form>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Contact Information */}
                        <div className="lg:col-span-2 space-y-6" data-testid="contact-info">
                            <Card className="bg-primary text-white border-0">
                                <CardContent className="p-8">
                                    <h3 className="text-xl font-bold mb-6 font-serif">Contact Information</h3>
                                    <div className="space-y-6">
                                        <a 
                                            href="mailto:infocravencanines@gmail.com" 
                                            className="flex items-start gap-4 hover:opacity-80 transition-opacity"
                                            data-testid="contact-email-link"
                                        >
                                            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <Mail className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Email</p>
                                                <p className="text-white/80">infocravencanines@gmail.com</p>
                                            </div>
                                        </a>
                                        
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                <MapPin className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium">Service Area</p>
                                                <p className="text-white/80">North Wiltshire and the surrounding areas</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-secondary/50 border-0">
                                <CardContent className="p-8">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Clock className="w-6 h-6 text-primary" />
                                        <h3 className="text-lg font-bold font-serif text-foreground">Response Time</h3>
                                    </div>
                                    <p className="text-muted-foreground">
                                        I aim to respond to all enquiries within 24-48 hours. 
                                        For urgent matters, please call or text.
                                    </p>
                                </CardContent>
                            </Card>

                            <Card className="border-primary/20">
                                <CardContent className="p-8">
                                    <h3 className="text-lg font-bold mb-4 font-serif text-foreground">What to Include</h3>
                                    <ul className="space-y-3 text-muted-foreground">
                                        <li className="flex items-start gap-2">
                                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                            <span>Your dog's name, breed, and age</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                            <span>What you're hoping to achieve</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                            <span>Any specific concerns or behaviours</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                            <span>Your preferred contact method</span>
                                        </li>
                                    </ul>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
